import { PERMISSIONS } from "./useSession";
import { holdsBackOfficePermission } from "./backOffice";
import type { SellerSession } from "@/lib/api/types";

/**
 * The top-level surfaces a session can land on after login: the three working
 * ones, and `driver` — not a working surface but the "use the Trioty driver
 * app" screen for a driver-only account (driver backend B1).
 */
export type LandingSurface = "pos" | "kitchen" | "manage" | "driver";

/**
 * Where a session belongs when it has not asked for a screen by name — after
 * login, and at the bare `/:locale` index.
 *
 * A hardcoded `/pos` landed a cook on the floor grid, every one of whose calls
 * 403s: the KITCHEN role holds `RESTAURANT_KITCHEN_VIEW` and no `POS_USE` at
 * all (see the `PERMISSIONS` note on the kitchen four). The same trap as the
 * old hardcoded `/manage/menu` that `ManageIndexRedirect` already fixed — a
 * door that opens onto a wall — so the fix is the same shape: land on the
 * first surface this session is actually allowed to use.
 *
 * Order is by how much of the job each surface is: the till first (it is the
 * whole shift for a waiter or cashier), then the kitchen board, then the back
 * office. `manage` is last AND the fallback because it is the only surface
 * that re-decides for itself — `ManageIndexRedirect` picks the first section
 * the role can see rather than assuming one.
 */
const SURFACES: readonly { surface: LandingSurface; permission: string }[] = [
  { surface: "pos", permission: PERMISSIONS.POS_USE },
  { surface: "kitchen", permission: PERMISSIONS.KITCHEN_VIEW },
];

export function landingSurfaceFor(
  permissions: readonly string[] | undefined | null,
): LandingSurface {
  const held = permissions ?? [];
  const working = SURFACES.find((candidate) =>
    held.includes(candidate.permission),
  )?.surface;
  if (working) return working;
  return isDriverOnly(held) ? "driver" : "manage";
}

/**
 * A DRIVER-ONLY account (driver backend B1): it holds `RESTAURANT_DRIVER_WORK`
 * and nothing this app serves — no till, no board, no back-office grant. Its
 * work is in the Trioty driver app, so every route here answers it with the
 * "use the driver app" screen rather than a till or a back office whose every
 * call 403s. A driver who has ALSO been given a till or a back-office grant
 * (a custom role, a second role) is not driver-only and lands as that grant
 * says. Owners and managers are never driver-only.
 */
export function isDriverOnly(
  permissions: readonly string[] | undefined | null,
): boolean {
  const held = permissions ?? [];
  return (
    held.includes(PERMISSIONS.DRIVER_WORK) &&
    !held.includes(PERMISSIONS.POS_USE) &&
    !held.includes(PERMISSIONS.KITCHEN_VIEW) &&
    !holdsBackOfficePermission(held)
  );
}

/** The same decision as a route path. `session` may be undefined — an unread
 *  session degrades to `/manage`, which never dead-ends. */
export function landingPathFor(
  locale: string,
  session: SellerSession | undefined,
): string {
  return `/${locale}/${landingSurfaceFor(session?.seller.permissions)}`;
}

/**
 * The landing for a session that may not be bound to its restaurant yet.
 *
 * `landingPathFor` reads permissions, and on an owner's DEFAULT store the
 * restaurant ones are simply absent — so asking it there answers "no till" for
 * the person who IS the cashier. A wrong binding is not a capability fact, it
 * is a question that has not been settled yet, so this refuses to answer it and
 * sends them to `/:locale`, where `LandingRedirect` waits for `useStoreBinding`
 * to repair the binding and then asks again with the right permissions.
 *
 * `/:locale` is also where an account with NO restaurant, or with several, has
 * to end up: `ProtectedLayout` owns the picker, the failure and the
 * no-restaurant explanation, and none of those belong on a login form.
 */
export function landingPathForSession(
  locale: string,
  session: SellerSession | undefined,
): string {
  if (session?.mustChangePassword) {
    return `/${locale}/change-password`;
  }
  if (session && session.seller.storeKind !== "RESTAURANT") {
    return `/${locale}`;
  }
  return landingPathFor(locale, session);
}
